import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal } from '@material-ui/core';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import {
  getUnitOperationGroups,
  getUnitOperations,
  getProjectGroups,
  getCodeTypes,
  getCodeGroups,
  getCodes,
  getUndergroundOperationControls,
  updateUndergroundOperationControls,
  setCurrentOperationControl,
  setOriginalCurrentOperationControl,
  setCurrentOperationControlFilter,
} from '~/store/underground/actions';
import useOperationalKanbanStyles from './useOperationControlKanbanStyles';
import { getElements, getSubElements } from '~/store/manager/actions';
import { FullscreenPortalContext } from './Kanban/context/FullscreenPortalContext';
import LoadingLayer from './Kanban/LoadingLayer';
import { TYPE_CARD } from './Kanban/dndTypes';
import { mapCard, moveCardByNeighbors } from './Kanban/helpers';
import OperationControlEditModal from '~/pages/OperationalControl/OperationControlEditModal';
import KanbanFilters from './KanbanFilters';
import HighlightsAccordion from './HighlightsAccordion';
import KanbanBoard from './Kanban/KanbanBoard';
import { buildColumnsByUnitOperation } from './Kanban/utils';
import { filterCards } from './KanbanFilters/filterLogic';
import KanbanInfoModal from './KanbanInfoModal';

const OperationalControlKanban = () => {
  const dispatch = useDispatch();
  const classes = useOperationalKanbanStyles();
  const handleFS = useFullScreenHandle();
  const fsRootRef = React.useRef(null);
  const firstGroupAppliedRef = useRef(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState(null);

  const [kanbanFilters, setKanbanFilters] = useState({
    id_unitary_operation_group: null,
    id_project_group: null,
    priority: null,
    id_element: null,
    element_point_name: '',
    id_code_type_ug: null,
    id_code_group_ug: null,
    code_ug_name: '',
  });

  useEffect(() => {
    dispatch(getUnitOperationGroups());
    dispatch(getProjectGroups());
    dispatch(getCodeTypes());
    dispatch(getCodeGroups());
    dispatch(getCodes());
    dispatch(getUnitOperations());
    dispatch(getElements());
    dispatch(getSubElements());
  }, [dispatch]);

  const isLoading = useSelector(state => state.loadingReducer.loading);
  const unitOperationGroups = useSelector(state => state.underground.unitOperationGroups);
  const unitOperations = useSelector(state => state.underground.unitOperations);
  const operationControls = useSelector(state => state.underground.operationControls);
  const projectGroups = useSelector(state => state.underground.projectGroups);
  const codeTypes = useSelector(state => state.underground.codeTypes);
  const codeGroups = useSelector(state => state.underground.codeGroups);
  const elements = useSelector(state => state.manager.elements);


  useEffect(() => {
    if (firstGroupAppliedRef.current) return;
    if (!unitOperationGroups.length) return;

    const firstId = unitOperationGroups[0]?.id;
    if (!firstId) return;

    firstGroupAppliedRef.current = true;

    setKanbanFilters(prev => ({
      ...prev,
      id_unitary_operation_group: firstId,
    }));
  }, [dispatch, unitOperationGroups]);

  useEffect(() => {
    const g = kanbanFilters.id_unitary_operation_group;

    if (!g) return;

    dispatch(getUndergroundOperationControls({ id_unitary_operation_group: g }));
  }, [dispatch, kanbanFilters.id_unitary_operation_group]);

  const unitOperationsFilteredByGroup = useMemo(() => {
    const g = kanbanFilters.id_unitary_operation_group;

    if (!g) return [];

    return (unitOperations || []).filter(uo => uo.id_unitary_operation_group === g);
  }, [unitOperations, kanbanFilters.id_unitary_operation_group]);

  const operationControlsColumns = useMemo(
    () => buildColumnsByUnitOperation(unitOperationsFilteredByGroup, operationControls),
    [unitOperationsFilteredByGroup, operationControls],
  );

  const [boards, setBoards] = useState(operationControlsColumns);
  useEffect(() => setBoards(operationControlsColumns), [operationControlsColumns]);

  const filteredColumns = useMemo(() => {
    const outCards = filterCards(boards || [], kanbanFilters);
    return outCards;
  }, [boards, kanbanFilters]);


  const updateCard = useCallback((card) => {
    dispatch(updateUndergroundOperationControls(card, kanbanFilters));
  }, [dispatch, kanbanFilters]);

  const handleCardClick = useCallback((card) => {
    dispatch(setCurrentOperationControl(card));
    dispatch(setOriginalCurrentOperationControl(card));
    dispatch(setCurrentOperationControlFilter(kanbanFilters));
    setModalContext('click');
    setModalOpen(true);
  }, [dispatch, kanbanFilters]);


  const handleClose = useCallback(() => {
    dispatch(setCurrentOperationControl(null));
    setModalOpen(false);
    // setSelectedCard(null);
  }, []);

  const getPortalContainer = React.useCallback(() => {
    if (handleFS.active && fsRootRef.current) {
      return fsRootRef.current;
    }
    return document.body;
  }, [handleFS.active, fsRootRef]);

  const unitOperationGroupsOptions = unitOperationGroups.map(
    uog => ({ label: uog.name, value: uog.id }),
  );

  const projectGroupsOptions = projectGroups.map(pg => ({ label: pg.name, value: pg.id })).sort(
    (a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }),
  );
  const codeTypesOptions = codeTypes.map(ct => ({ label: ct.name, value: ct.id })).sort(
    (a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }),
  );
  const codeGroupsOptions = codeGroups.map(cg => ({ label: cg.name, value: cg.id })).sort(
    (a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }),
  );
  const elementsOptions = elements.map(c => ({ label: c.name, value: c.id })).sort(
    (a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }),
  );

  const kanbanFiltersOptions = {
    unitOperationGroupOpts: [...unitOperationGroupsOptions],
    projectGroupOpts: [{ label: 'Todos', value: null }, ...projectGroupsOptions],
    codeTypeOpts: [{ label: 'Todos', value: null }, ...codeTypesOptions],
    codeGroupOpts: [{ label: 'Todos', value: null }, ...codeGroupsOptions],
    elementOpts: [{ label: 'Todos', value: null }, ...elementsOptions],
  };

  const kanbanFiltersCleanDefault = {
    id_unitary_operation_group: null,
    id_project_group: null,
    priority: null,
    id_element: null,
    element_point_name: '',
    id_code_type_ug: null,
    id_code_group_ug: null,
    code_ug_name: '',
  };

  const onClearFilters = useCallback(() => {
    setKanbanFilters(prev => ({
      ...kanbanFiltersCleanDefault,
      id_unitary_operation_group: prev.id_unitary_operation_group,
    }));
  }, []);

  const handleInfoModal = useCallback(() => {
    setInfoModalOpen(!infoModalOpen);
  }, [infoModalOpen]);

  return (
    <DndProvider backend={HTML5Backend}>
      <FullScreen
        handle={handleFS}
        style={{
          width: '100% !important',
          height: '100% !important',
          display: 'flex !important',
          flexDirection: 'column !important',
        }}
      >
        <div className={classes.root}>
          <div
            ref={fsRootRef}
            className={classes.scrollContainer}
            style={{
              padding: handleFS.active ? '10px' : null,
            }}
          >
            <FullscreenPortalContext.Provider value={getPortalContainer}>
              <LoadingLayer open={isLoading && handleFS?.active} label="Salvando alterações..." />

              <div className={classes.contentMinWidth}>
                <div className={classes.stickyFilters}>
                  <KanbanFilters
                    values={kanbanFilters}
                    onChange={
                      (name, value) => setKanbanFilters(prev => ({ ...prev, [name]: value }))
                    }
                    onClear={onClearFilters}
                    isFullScreenActive={handleFS}
                    menuContainerEl={fsRootRef.current}
                    options={kanbanFiltersOptions}
                    order={[
                      'id_unitary_operation_group', 'id_project_group', 'priority', 'id_element', 'element_point_name',
                      'id_code_type_ug', 'id_code_group_ug', 'code_ug_name',
                    ]}
                    handleInfoModal={handleInfoModal}
                  />
                </div>

                <div className={classes.accordion}>
                  <HighlightsAccordion
                    rawLines={operationControls}
                    defaultExpanded={false}
                    handleClick={handleCardClick}
                    onUpdateCard={updateCard}
                  />
                </div>
                <div className={classes.kanbanScroll}>
                  <KanbanBoard
                    columns={filteredColumns}
                    onMove={(payload) => {
                      if (payload.afterId === null) {
                        dispatch(setCurrentOperationControl({
                          ...payload.card,
                          id_unitary_operation: payload.toColId,
                        }));
                        setModalContext('drag');
                        setModalOpen(true);
                      }
                    }}
                    onPatch={(cardId, patch) => {
                      const holder = { current: null };
                      setBoards(prev => mapCard(prev, cardId, (c) => {
                        const next = { ...c, ...patch };
                        holder.current = next;
                        return next;
                      }));
                      if (holder.current) updateCard(holder.current);
                    }}
                    onCardClick={handleCardClick}
                    typeCard={TYPE_CARD}
                  />
                </div>
              </div>
            </FullscreenPortalContext.Provider>
          </div>
          <Modal
            open={modalOpen}
            onClose={(_, type) => type === 'backdrop' && setModalOpen(false)}
            container={getPortalContainer}
            className={classes.modalContainer}
          >
            <div>
              <OperationControlEditModal
                handleClose={handleClose}
                context={modalContext}
                validationErrors
              />
            </div>
          </Modal>
          <Modal open={infoModalOpen} container={getPortalContainer}>
            <KanbanInfoModal handleInfoModal={handleInfoModal} />
          </Modal>
        </div>
      </FullScreen>
    </DndProvider>
  );
};

export default OperationalControlKanban;
